import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { api } from '../../../services/api';

import InputChip from '../../ui/form-elements/text-input/input-chip';
import DropdownToggleWrapper from '../../ui/dropdown/dropdown-toggle-wrapper';
import ProductModal from '../strategies/product-modal';
import loadingSpinner from '../../../images/icons/loader.png';

import '../../ui/form-elements/text-input/text-input.scss';

const SKUInput = props => {
  const { name, value, targetList, onChange, maxlength } = props;
  const [inputValue, setInputValue] = useState('');
  const [invalidCount, setInvalidCount] = useState(0);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const { merchantId } = useParams();

  const dropdownItems = targetList.map(target => {
    return {
      label: target.target_product_name || 'Product Information Unavailable',
      value: target.target_sku,
    };
  });

  const validateSKUs = values => {
    const validateEndpoint = `/api/v0/merchants/${merchantId}/strategy/validate_products/`;
    const skuQueryString = values.join(',');
    setIsValidating(true);

    api
      .get(validateEndpoint, { skus: skuQueryString })
      .then(resp => {
        const data = resp.data.data[0].stats;
        setValue(formatSKUProducts(data.valid_skus));
        setInvalidCount(data.invalid_skus.length);
        setIsValidating(false);
      })
      .catch(() => {
        setErrorMessage('Something went wrong. Please try again');
        setIsValidating(false);
      });
  };

  const formatSKUProducts = products => {
    return products.map(item => ({
      target_sku: item.sku,
      target_product_name: `${item.brand} - ${item.name}`,
    }));
  };

  const handleChipify = e => {
    if (!inputValue) return;
    const newValues = inputValue.replace(/ /g, '').split(',');

    validateSKUs([...value, ...newValues]);
    setInputValue('');
  };

  const handleKeyUp = e => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleChipify(e);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const textValue = e.clipboardData.getData('Text');
    const newValues = textValue
      .replace(/ /g, '')
      .split(',')
      .map(text => text.substring(0, 512));

    validateSKUs([...value, ...newValues]);
    setInputValue('');
  };

  const handleSelect = selected => {
    const newValues = selected.map(item => ({
      target_sku: item.value,
      target_product_name: item.label,
    }));
    setValue(newValues);
  };

  const setValue = value => {
    onChange({ target: { value, name } });
  };

  const setTargets = (stats, totalErrors) => {
    setValue([...targetList, ...formatSKUProducts(stats)]);
    setInvalidCount(totalErrors);
  };

  return (
    <div className="chipped-input-container">
      <div className="chipped-input">
        {value.length > 0 && (
          <DropdownToggleWrapper
            items={dropdownItems}
            multi={true}
            confirmPrompt={true}
            onSelect={handleSelect}
            selected={dropdownItems}
            itemRenderer={({ label, value }) => {
              return (
                <span>
                  <span className="chip-label">{label}</span>
                  <span className="chip-value">{value}</span>
                </span>
              );
            }}
          >
            <InputChip
              text={`SKU IDs (${value.length})`}
              size="text"
              onRemove={() => setValue([])}
            />
          </DropdownToggleWrapper>
        )}
        <div>
          {isValidating ? (
            <div className="loader-container-v2">
              <img src={loadingSpinner} alt="Loading" />
            </div>
          ) : (
            <input
              type="text"
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
              }}
              onBlur={handleChipify}
              onPaste={handlePaste}
              onKeyUp={handleKeyUp}
              onKeyDown={e => {
                if (e.keyCode === 13) e.preventDefault();
              }}
              onSubmit={e => e.preventDefault()}
              maxlength={maxlength}
            />
          )}
        </div>
      </div>
      <div className="chipped-input-sublabel">
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="upload-csv" onClick={() => setShowCSVModal(true)}>
          Upload CSV
        </div>
        <div className="paste-message">Paste in a list of Product SKU IDs separated by commas.</div>
        {invalidCount > 0 && (
          <div className="error-message">({invalidCount}) invalid SKUs removed</div>
        )}
      </div>
      {showCSVModal && (
        <ProductModal
          triggers={[]}
          setShowModal={setShowCSVModal}
          confirmTriggers={setTargets}
          useIncreasedLimit={true}
        />
      )}
    </div>
  );
};

export default SKUInput;
